import type {
  ActivitySessionSplitPricing,
  ActivitySessionSplitShare,
} from "@/db/schema/activity-session-split";

// ──────────────────────────────────────────────────────────────────────────────
// SESSION SPLIT MATH
//
// Pure, no I/O, safe on the client. The per-head figure is computed here from
// the live roster on every read and never persisted — there is no materialised
// number to drift. Only `blockedMinor` (a real ledger position) and, at close,
// `amountMinor` (the final charge) are stored.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * How long after `endTime` the organizer has to reconcile before the split
 * locks itself on the full roster and whatever costs are recorded by then.
 *
 * This is a reconciliation window, not a roster tick. The end of the game is
 * when the organizer starts knowing what it cost, not when they finish knowing:
 * the pitch was ₹1,200 and everyone knew that walking in, but somebody kept
 * ordering energy drinks and the tab came to ₹640. That number only exists
 * afterwards, and until it is in, the split is wrong.
 *
 * The window exists at all because the lock is IRREVERSIBLE in Hiive Money mode
 * — the credits are in the organizer's wallet and there is no user-to-user
 * transfer to bring them back — so every correction has to happen before
 * settlement, which is exactly why it has to be long enough to hold the real
 * reconciliation.
 *
 * One flat 48 hours rather than a short default with an extension: the
 * extension was machinery in service of an edge case. Two days covers a Friday
 * game reconciled on Sunday, and an organizer who is ready in ten minutes just
 * taps "Lock it in".
 */
export const SPLIT_CLOSE_GRACE_MS = 48 * 60 * 60 * 1000;

/** Default share of the base an organizer may add back as extras. */
export const SPLIT_DEFAULT_EXTRAS_ALLOWANCE_PERCENT = 50;

/**
 * Past this fraction of the allowance a receipt stops being optional — the
 * point where "a few drinks" has become something worth evidencing.
 */
export const SPLIT_EXTRAS_RECEIPT_THRESHOLD = 0.5;

/** A dispute reaches the admin queue only after the organizer has had this long. */
export const SPLIT_DISPUTE_ESCALATION_MS = 48 * 60 * 60 * 1000;

/** Nudge ladder, in days after close. Front-loaded, then tapering to silence. */
export const SPLIT_NUDGE_OFFSETS_DAYS = [1, 3, 7, 14, 30, 60] as const;

/** From this stage on, the debt starts showing as a reliability signal. */
export const SPLIT_RELIABILITY_STAGE = 4; // the +14d nudge

/** Unpaid rows become visible to the rest of the session this long after close. */
export const SPLIT_PEER_VISIBILITY_MS = 24 * 60 * 60 * 1000;

/** Profile-level reliability signals start here. Amounts are never shown. */
export const SPLIT_PROFILE_VISIBILITY_MS = 14 * 24 * 60 * 60 * 1000;

export type SplitPricingShape =
  | { pricing: "divide_total"; totalAmountMinor: number }
  | { pricing: "fixed_per_person"; perPersonAmountMinor: number };

export const splitPricingShape = (split: {
  pricing: ActivitySessionSplitPricing;
  totalAmountMinor: number | null;
  perPersonAmountMinor: number | null;
}): SplitPricingShape =>
  split.pricing === "divide_total"
    ? { pricing: "divide_total", totalAmountMinor: split.totalAmountMinor ?? 0 }
    : {
        pricing: "fixed_per_person",
        perPersonAmountMinor: split.perPersonAmountMinor ?? 0,
      };

/**
 * How many people the cost divides between.
 *
 * The organizer paid the venue, so their own share is already accounted for and
 * their row is settled from the start — but they are still in the divisor when
 * they play. ₹1,200 among six is ₹200 each, not ₹240. At a fixed price they are
 * usually the coach rather than a participant, so `organizerPlaying` defaults
 * off there and they drop out of the count entirely.
 */
export function payingHeadcount(input: {
  rosterUserIds: readonly string[];
  organizerUserId: string;
  organizerPlaying: boolean;
}): number {
  const unique = new Set(input.rosterUserIds);
  if (!input.organizerPlaying) unique.delete(input.organizerUserId);
  return unique.size;
}

/**
 * Largest-remainder allocation, in minor units.
 *
 * ₹1,000 among 3 → 33333, 33333, 33334 paise. The shares always sum to EXACTLY
 * the total, so there is no drift and nobody has to absorb a rounding gap.
 *
 * `orderedIds` must be stable (share id ascending). The per-head figure moves
 * constantly as people join, and you do not want the extra paise hopping
 * between people on every recomputation — earliest joiners carry it, and at one
 * paise each it is invisible.
 */
export function allocateLargestRemainder(
  totalMinor: number,
  orderedIds: readonly number[],
): Map<number, number> {
  const out = new Map<number, number>();
  const n = orderedIds.length;
  if (n === 0) return out;
  const total = Math.max(0, Math.round(totalMinor));
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  orderedIds.forEach((id, index) => {
    out.set(id, index < remainder ? base + 1 : base);
  });
  return out;
}

/**
 * The headline per-head figure for a given headcount. This is the LARGEST share
 * (base + 1 where there is a remainder), because everything user-facing built on
 * it is a ceiling promise — "nobody pays more than this".
 */
export function previewPerHeadMinor(
  shape: SplitPricingShape,
  headcount: number,
): number {
  if (shape.pricing === "fixed_per_person") return shape.perPersonAmountMinor;
  if (headcount <= 0) return shape.totalAmountMinor;
  return Math.ceil(shape.totalAmountMinor / headcount);
}

/**
 * `minParticipants` is the minimum number of people who are actually playing.
 * A non-playing organizer is present in the session's participant table for
 * ownership/chat purposes, but is not one of those players. Keeping the
 * organizer out of both the minimum and the divisor makes "minimum 6 at ₹300"
 * mean six paying people and ₹1,800 collected.
 */
export const minPayingCount = (
  minParticipants: number,
  _organizerPlaying: boolean,
): number => Math.max(1, minParticipants);

/**
 * The most this person could ever be asked for — and therefore exactly what we
 * block at join in Hiive Money mode.
 *
 * Fixed price: the price. Nothing to release, no gap to explain to anyone. These
 * sessions get the guarantee at zero conceptual cost, which is why they are the
 * cleanest thing to launch Hiive Money mode with.
 *
 * Splitting a cost: `total ÷ min`. A session below its minimum is cancelled at
 * start, so the per-head figure can never climb past that. Blocking this ceiling
 * rather than the current per-head is what lets the block sit still for the whole
 * session: if we blocked today's ₹200 and someone dropped, we would have to go
 * back for another ₹40 from a wallet that may have been spent — and the guarantee
 * would be a lie the first time it mattered.
 */
export function worstCaseShareMinor(input: {
  shape: SplitPricingShape;
  minParticipants: number;
  organizerPlaying: boolean;
}): number {
  if (input.shape.pricing === "fixed_per_person") {
    return input.shape.perPersonAmountMinor;
  }
  return Math.ceil(
    input.shape.totalAmountMinor /
      minPayingCount(input.minParticipants, input.organizerPlaying),
  );
}

// ── Extras (§3, §4) ──────────────────────────────────────────────────────────

export type ExtrasPolicy = {
  extrasAllowed: boolean;
  extrasAllowancePercent: number;
};

/**
 * The most that can ever be added to ONE person's row after the whistle.
 *
 * Floored rather than rounded, and computed from the same worst-case figure the
 * block is sized on, so `worstCase + allowance` is exactly `blocked` with no
 * off-by-one to reason about.
 */
export function extrasAllowanceMinor(
  worstCaseShareMinorValue: number,
  policy: ExtrasPolicy,
): number {
  if (!policy.extrasAllowed) return 0;
  const percent = Math.min(100, Math.max(0, policy.extrasAllowancePercent));
  return Math.floor((worstCaseShareMinorValue * percent) / 100);
}

/**
 * What we actually block at join: the worst-case share plus the declared extras
 * allowance.
 *
 * This is the move that lets the guarantee survive extras. Because the ceiling
 * is declared before anyone joins, we can hold money against it — so the
 * organizer gets to add the drinks tab and it still comes out of a hold the
 * participant knowingly agreed to. Nothing has to be chased afterwards.
 *
 * The cost is a bigger number on the join screen — ₹450 blocked instead of ₹300
 * — and that will put some people off. It is the right trade anyway: the
 * alternative is a smaller number now and a WhatsApp chase later, which is the
 * status quo we are replacing.
 */
export function blockedShareMinor(input: {
  shape: SplitPricingShape;
  minParticipants: number;
  organizerPlaying: boolean;
  policy: ExtrasPolicy;
}): number {
  const worstCase = worstCaseShareMinor(input);
  return worstCase + extrasAllowanceMinor(worstCase, input.policy);
}

/**
 * Spread one cost line across the people on it.
 *
 * Same largest-remainder rule as the base, ordered by the same stable key, so
 * every line sums to exactly its own amount and therefore so does the grand
 * total — no drift, nobody absorbing a stray paise.
 */
export function allocateExtraEqually(
  amountMinor: number,
  orderedUserIds: readonly string[],
): Map<string, number> {
  const out = new Map<string, number>();
  const n = orderedUserIds.length;
  if (n === 0) return out;
  const total = Math.max(0, Math.round(amountMinor));
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  orderedUserIds.forEach((userId, index) => {
    out.set(userId, index < remainder ? base + 1 : base);
  });
  return out;
}

/**
 * Re-spread a hand-entered line when somebody on it is unticked as a no-show.
 *
 * Their part goes across whoever is left in that line's set, in proportion to
 * what they were already down for — the least surprising reading of "Rohan had
 * four and I had a lime soda, and Rohan didn't turn up". Silently splitting it
 * across people who were never on the line would be worse.
 */
export function reallocateExactExtra(
  amountMinor: number,
  entries: ReadonlyArray<{ userId: string; amountMinor: number }>,
): Map<string, number> {
  const out = new Map<string, number>();
  if (entries.length === 0) return out;
  const weightTotal = entries.reduce(
    (sum, entry) => sum + Math.max(0, entry.amountMinor),
    0,
  );
  // Everyone left was down for nothing — there is no proportion to preserve, so
  // fall back to an equal split rather than dropping the money.
  if (weightTotal <= 0) {
    return allocateExtraEqually(
      amountMinor,
      entries.map((entry) => entry.userId),
    );
  }
  const total = Math.max(0, Math.round(amountMinor));
  let assigned = 0;
  entries.forEach((entry, index) => {
    const isLast = index === entries.length - 1;
    const value = isLast
      ? total - assigned
      : Math.floor((total * Math.max(0, entry.amountMinor)) / weightTotal);
    out.set(entry.userId, value);
    assigned += value;
  });
  return out;
}

/**
 * What the organizer actually collects FROM OTHER PEOPLE.
 *
 * Splitting a cost, this is capped at what the venue cost them, less their own
 * share if they are playing — they are recovering, not earning. At a fixed price
 * it scales with turnout, which is the whole point of charging one.
 *
 * `payingCount` is the divisor, so it already excludes the organizer when they
 * are not playing.
 */
export function organizerCollectsMinor(
  shape: SplitPricingShape,
  payingCount: number,
  organizerPlaying: boolean,
): number {
  if (shape.pricing === "fixed_per_person") {
    const others = Math.max(0, payingCount - (organizerPlaying ? 1 : 0));
    return shape.perPersonAmountMinor * others;
  }
  if (payingCount <= 0) return 0;
  const ownShare = organizerPlaying
    ? Math.floor(shape.totalAmountMinor / payingCount)
    : 0;
  return shape.totalAmountMinor - ownShare;
}

/**
 * The amount a share actually settles at.
 *
 * A HARD CEILING, not a policy: never more than the terms that person last
 * consented to. Because non-responders come off the roster rather than being
 * kept on old terms (§8), everyone still in the split has agreed to the current
 * number and this should never bind. That is the point — it makes "nobody is
 * ever charged more than they agreed to" true by construction instead of
 * depending on every code path remembering to check.
 */
export function chargeableShareMinor(
  computedMinor: number,
  agreedAmountMinor: number | null,
): number {
  if (agreedAmountMinor == null) return computedMinor;
  return Math.min(computedMinor, agreedAmountMinor);
}

/**
 * The two ceilings, applied separately, and their sum.
 *
 * They have to bind independently rather than as one total. Unticking no-shows
 * at the lock pushes the base per-head ABOVE the worst case — ₹1,200 among
 * three when the minimum was four — and `agreedAmountMinor` is what catches
 * that. Extras are bounded by the declared allowance. Folding them into a
 * single number would let an unusually empty roster quietly eat the extras
 * headroom and charge somebody a base they never agreed to.
 *
 * Since the two ceilings sum to exactly `blockedMinor`, this is also the
 * statement of the invariant the whole extras design rests on: the final amount
 * can never exceed what was blocked at join.
 */
export function chargeableTotalMinor(input: {
  baseMinor: number;
  extrasMinor: number;
  agreedAmountMinor: number | null;
  agreedExtrasMinor: number;
}): { baseMinor: number; extrasMinor: number; totalMinor: number } {
  const baseMinor = chargeableShareMinor(
    input.baseMinor,
    input.agreedAmountMinor,
  );
  const extrasMinor = Math.min(
    Math.max(0, input.extrasMinor),
    Math.max(0, input.agreedExtrasMinor),
  );
  return { baseMinor, extrasMinor, totalMinor: baseMinor + extrasMinor };
}

/**
 * Re-consent window: half the time left until the session starts, floored at an
 * hour and capped at two days, and never landing after the session begins.
 *
 * A change two weeks out does not need an answer tonight; a change on the
 * morning of the game does. The roster has to be real by kick-off, so the
 * deadline can never outlive the start.
 */
export function consentDeadlineFor(input: {
  now: Date;
  startTime: Date;
}): Date {
  const msUntilStart = input.startTime.getTime() - input.now.getTime();
  const HOUR = 60 * 60 * 1000;
  const window = Math.min(Math.max(msUntilStart / 2, HOUR), 48 * HOUR);
  const deadline = new Date(input.now.getTime() + window);
  return deadline > input.startTime ? input.startTime : deadline;
}

// ── Share predicates ─────────────────────────────────────────────────────────

export type ShareLike = Pick<
  ActivitySessionSplitShare,
  "status" | "settlementMode" | "settledVia"
>;

/** Still owes something. Waived and settled rows are done; disputed ones are frozen. */
export const shareIsOutstanding = (share: ShareLike): boolean =>
  share.status === "open" ||
  share.status === "blocked" ||
  share.status === "awaiting_confirmation";

/** This row carries the Hiive Money guarantee. */
export const shareIsGuaranteed = (share: ShareLike): boolean =>
  share.settlementMode === "hiive_money" &&
  (share.status === "blocked" ||
    (share.status === "settled" && share.settledVia === "hiive_money"));

/**
 * A contested row stops feeding reliability signals and stops being nudged.
 * A dispute is not evidence of anything; the PATTERN across sessions is, which
 * is where that pressure belongs.
 */
export const shareIsContested = (share: ShareLike): boolean =>
  share.status === "disputed";

// ── Copy helpers ─────────────────────────────────────────────────────────────

/**
 * The two pricing types are different products wearing the same two fields, and
 * the minimum is the one people set carelessly. Splitting a cost, setting the
 * minimum IS setting the maximum anyone will pay, and the UI should say that
 * outright rather than making an organizer reason about `total ÷ min`.
 */
export function minimumMeaning(input: {
  shape: SplitPricingShape;
  minParticipants: number;
  organizerPlaying: boolean;
  formatMinor: (minor: number) => string;
}): { headline: string; detail: string } {
  const { shape, minParticipants, formatMinor } = input;
  if (shape.pricing === "fixed_per_person") {
    const floor = organizerCollectsMinor(
      shape,
      minPayingCount(minParticipants, input.organizerPlaying),
      input.organizerPlaying,
    );
    return {
      headline: `Minimum ${minParticipants} → you'll collect at least ${formatMinor(floor)}`,
      detail: `Below ${minParticipants} it's cancelled. Your price stays ${formatMinor(shape.perPersonAmountMinor)} either way.`,
    };
  }
  const ceiling = worstCaseShareMinor({
    shape,
    minParticipants,
    organizerPlaying: input.organizerPlaying,
  });
  return {
    headline: `Minimum ${minParticipants} → nobody pays more than ${formatMinor(ceiling)}`,
    detail: `Below ${minParticipants} the session is cancelled and everyone's money is released.`,
  };
}

/**
 * Sharing works in opposite directions. Splitting a cost, "bring someone and
 * everyone pays less" is a genuine recruiting loop. At a fixed price that
 * framing is a lie — nobody's price moves — so it becomes spots remaining, aimed
 * at the organizer, who is the one who gains.
 */
export function shareInviteCopy(input: {
  shape: SplitPricingShape;
  payingCount: number;
  maxParticipants: number;
  formatMinor: (minor: number) => string;
}): string {
  const { shape, payingCount, maxParticipants, formatMinor } = input;
  if (shape.pricing === "fixed_per_person") {
    const left = Math.max(0, maxParticipants - payingCount);
    return left > 0
      ? `${left} spot${left === 1 ? "" : "s"} left at ${formatMinor(shape.perPersonAmountMinor)} each`
      : "Full";
  }
  const now = previewPerHeadMinor(shape, payingCount);
  const next = previewPerHeadMinor(shape, payingCount + 1);
  return next < now
    ? `${formatMinor(now)} each — one more player and it's ${formatMinor(next)}`
    : `${formatMinor(now)} each`;
}
